package dev.lingvo.site.pages.app.settings.billing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.Plan
import dev.lingvo.shared.PlanChangeAcceptedDto
import dev.lingvo.shared.PlanChangeMoneyDto
import dev.lingvo.shared.PlanChangePreviewDto
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf
import kotlin.js.Date
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/** The component's lifetime is keyed by workspace/role at its call site. */
@Composable
internal fun PlanChangeFlow(
    current: Plan,
    targets: List<Plan>,
    previewChange: suspend (Plan) -> PlanChangePreviewDto,
    confirmChange: suspend (String) -> PlanChangeAcceptedDto,
) {
    val scope = rememberCoroutineScope()
    var quote by remember { mutableStateOf<PlanChangePreviewDto?>(null) }
    var busy by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var terminal by remember { mutableStateOf(false) }
    var accepted by remember { mutableStateOf(false) }
    var now by remember { mutableStateOf(Date.now()) }
    LaunchedEffect(quote?.previewId) {
        now = Date.now()
        while (quote != null) { delay(1000); now = Date.now() }
    }
    Column(Modifier.gap(Space.MD)) {
        message?.let {
            LAlert(
                if (accepted) t("app.settings.billing.plan_change_flow.change_accepted", "Change accepted") else t("app.settings.billing.plan_change_flow.check_your_plan_change", "Check your plan change"),
                it, variant = if (accepted) LAlertVariant.INFO else LAlertVariant.WARNING, live = true,
            )
        }
        if (terminal) {
            LLinkButton(t("app.settings.billing.plan_change_flow.check_billing_status", "Check billing status"), "/app/settings/billing", variant = LButtonVariant.SECONDARY)
        } else {
            val preview = quote
            if (preview == null) {
                targets.forEach { target ->
                    LButton(tf("billing.change.review_target", "Review change to {plan}", "plan" to target.displayName), {
                        if (!busy) {
                            busy = true
                            message = null
                            scope.launch {
                                try {
                                    val result = previewChange(target)
                                    if (result.currentPlan != current || result.targetPlan != target || !hasDisplayablePlanQuote(result)) {
                                        message = tr("app.settings.billing.plan_change_flow.the_full_estimate_is_unavailable_refresh", "The full estimate is unavailable. Refresh billing before continuing.")
                                    } else quote = result
                                } catch (cancelled: CancellationException) { throw cancelled
                                } catch (_: Exception) {
                                    message = tr("app.settings.billing.plan_change_flow.the_estimate_could_not_be_loaded", "The estimate could not be loaded. No plan-change request was submitted.")
                                } finally { busy = false }
                            }
                        }
                    }, loading = busy, enabled = !busy, variant = LButtonVariant.SECONDARY)
                }
            } else {
                val expires = Date.parse(preview.expiresAt!!)
                val expired = !expires.isFinite() || now >= expires
                val regionLabel = t("app.settings.billing.plan_change_flow.plan_change_estimate", "Plan change estimate")
                LCard {
                    Div(attrs = {
                        attr("role", "region")
                        attr("aria-label", regionLabel)
                        attr("tabindex", "-1")
                        ref { element -> element.focus(); onDispose { } }
                    }) {
                        Column(Modifier.gap(Space.MD)) {
                            LSectionTitle(tf("billing.change.review_heading", "Review {plan}", "plan" to preview.targetPlan.displayName))
                            LMuted(tf("billing.change.seats", "For {seats} owners and admins on this workspace.", "seats" to preview.seats.toString()))
                            QuoteAmount(if (preview.prorationAction == "credit") t("app.settings.billing.plan_change_flow.proration_credit", "Proration credit") else t("app.settings.billing.plan_change_flow.proration_charge", "Proration charge"), preview.proration)
                            preview.immediateTotal?.let { QuoteAmount(t("app.settings.billing.plan_change_flow.immediate_transaction_total", "Immediate transaction total"), it) }
                                ?: LMuted(t("app.settings.billing.plan_change_flow.no_immediate_transaction_is_included_in", "No immediate transaction is included in this estimate."))
                            preview.nextTotal?.let { QuoteAmount(t("app.settings.billing.plan_change_flow.next_transaction_total", "Next transaction total"), it) }
                            QuoteAmount(t("app.settings.billing.plan_change_flow.recurring_total", "Recurring total"), preview.recurringTotal)
                            LMuted(tf("billing.change.renewal", "Next billing date: {date}", "date" to Date(preview.nextBilledAt).toLocaleDateString()))
                            LMuted(tf("billing.change.expiry_time", "Estimate expires: {time}", "time" to Date(expires).toLocaleString()))
                            LMuted(t("app.settings.billing.plan_change_flow.the_change_applies_immediately_if_accepted", "The change applies immediately if accepted. These are the payment provider's estimates, including any returned taxes. Confirmation checks the terms again; changed terms need another review."))
                            if (expired) LAlert(t("app.settings.billing.plan_change_flow.this_estimate_expired", "This estimate expired"), t("app.settings.billing.plan_change_flow.request_a_fresh_estimate_before_confirming", "Request a fresh estimate before confirming."), live = true)
                            LButton(t("app.settings.billing.plan_change_flow.confirm_plan_change", "Confirm plan change"), {
                                if (!busy && Date.now() < expires) {
                                    busy = true
                                    scope.launch {
                                        try {
                                            confirmChange(preview.previewId!!)
                                            accepted = true
                                            terminal = true
                                            message = tr("app.settings.billing.plan_change_flow.the_payment_provider_accepted_your_request", "The payment provider accepted your request. Your workspace plan is updating; check billing for the confirmed status.")
                                        } catch (cancelled: CancellationException) { throw cancelled
                                        } catch (failure: Exception) {
                                            val safeToReview = (failure as? ApiException)?.code in setOf("preview_changed", "subscription_changed", "preview_unavailable", "plan_not_available", "billing_busy")
                                            terminal = !safeToReview
                                            message = if (safeToReview) tr("app.settings.billing.plan_change_flow.the_estimate_is_no_longer_available", "The estimate is no longer available. Refresh billing and review the current terms before submitting again.")
                                                else tr("app.settings.billing.plan_change_flow.the_outcome_is_not_confirmed_do", "The outcome is not confirmed. Do not submit again. Check billing or contact support@lingvo.dev while we reconcile the request.")
                                        } finally { quote = null; busy = false }
                                    }
                                }
                            }, enabled = !busy && !expired, loading = busy)
                            LButton(t("app.settings.billing.plan_change_flow.back_to_plans", "Back to plans"), { quote = null }, enabled = !busy, variant = LButtonVariant.GHOST)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun QuoteAmount(label: String, money: PlanChangeMoneyDto) {
    Div { Text(tf("billing.change.amount", "{label}: {amount}", "label" to label, "amount" to money.display.orEmpty())) }
}

internal fun hasDisplayablePlanQuote(preview: PlanChangePreviewDto): Boolean =
    !preview.previewId.isNullOrBlank() && !preview.expiresAt.isNullOrBlank() &&
        Date.parse(preview.expiresAt.orEmpty()).isFinite() && Date.parse(preview.nextBilledAt).isFinite() &&
        preview.seats > 0 && preview.prorationAction in setOf("credit", "charge") &&
        listOfNotNull(preview.proration, preview.immediateTotal, preview.nextTotal, preview.recurringTotal)
            .all { !it.display.isNullOrBlank() && it.currency == preview.proration.currency }
